import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import type { GestorEquipeFeriasCalendario } from '@/services/gestoresService'

export type PjFeriasSituacao = GestorEquipeFeriasCalendario['membros'][number]['situacao']

export function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function eventCoversDay(inicio: string, fim: string, day: Date): boolean {
  const start = new Date(inicio)
  const end = new Date(fim)
  const cursor = new Date(day)
  cursor.setHours(12, 0, 0, 0)
  start.setHours(0, 0, 0, 0)
  end.setHours(23, 59, 59, 999)
  return cursor >= start && cursor <= end
}

export function buildEventCountByDay(
  eventos: GestorEquipeFeriasCalendario['eventos'],
  viewMonth: Date,
): Map<string, number> {
  const monthStart = startOfMonth(viewMonth)
  const monthEnd = endOfMonth(viewMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })
  const counts = new Map<string, number>()
  const visiveis = eventos.filter(isFeriasEventoVisivel)

  for (const day of days) {
    const key = dayKey(day)
    const total = visiveis.filter((evento) => eventCoversDay(evento.inicio, evento.fim, day)).length
    if (total > 0) counts.set(key, total)
  }

  return counts
}

export function eventsOnDay(
  eventos: GestorEquipeFeriasCalendario['eventos'],
  day: Date | null,
) {
  if (!day) return []
  return eventos.filter((evento) => eventCoversDay(evento.inicio, evento.fim, day))
}

export function calendarDaysForMonth(viewMonth: Date): Date[] {
  const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 })
  const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 })
  return eachDayOfInterval({ start, end })
}

export const FERIAS_SITUACAO_META: Record<
  PjFeriasSituacao,
  { label: string; hint: string; tone: string }
> = {
  elegivel: {
    label: 'Pode pedir férias',
    hint: 'Elegível e com saldo disponível',
    tone: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  },
  atrasada: {
    label: 'Férias atrasadas',
    hint: 'Elegível há mais de 6 meses e ainda não tirou',
    tone: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  },
  pendente: {
    label: 'Aguardando elegibilidade',
    hint: 'Ainda não cumpriu 12 meses ou 12 NFs',
    tone: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
  },
  sem_saldo: {
    label: 'Sem saldo de dias',
    hint: 'Cota de 20 dias já utilizada',
    tone: 'text-text-muted bg-white/[0.04] border-[var(--glass-border)]',
  },
  inativo: {
    label: 'Inativo',
    hint: 'Profissional não está ativo',
    tone: 'text-text-muted bg-white/[0.04] border-[var(--glass-border)]',
  },
}

export const FERIAS_EVENTO_META: Record<
  GestorEquipeFeriasCalendario['eventos'][number]['tipo'],
  { label: string; className: string }
> = {
  ferias_registrada: {
    label: 'Férias registradas',
    className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  },
  solicitacao_p6: {
    label: 'Solicitação de Ausência',
    className: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  },
  solicitacao_p3: {
    label: 'Solicitação descanso PJ',
    className: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
  },
}

export function eventoDisplayMeta(evento: FeriasEvento): { label: string; className: string } {
  const categoria = classifyFeriasEvento(evento)
  if (evento.tipo === 'ferias_registrada') {
    if (categoria === 'programada') {
      return {
        label: 'Férias programadas',
        className: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
      }
    }
    if (categoria === 'em_andamento') {
      return {
        label: 'Férias em andamento',
        className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      }
    }
  }
  return FERIAS_EVENTO_META[evento.tipo]
}

export type FeriasEvento = GestorEquipeFeriasCalendario['eventos'][number]
export type FeriasEventoCategoria = 'programada' | 'em_andamento' | 'historica'

const FERIAS_STATUS_OCULTOS = new Set(['REJEITADO', 'CANCELADO'])
const CHAMADO_STATUS_OCULTOS = new Set(['CANCELADO', 'CONCLUIDO'])

export function isFeriasEventoVisivel(evento: FeriasEvento): boolean {
  if (evento.tipo === 'ferias_registrada') {
    return !FERIAS_STATUS_OCULTOS.has(evento.status)
  }
  return !CHAMADO_STATUS_OCULTOS.has(evento.status)
}

export function classifyFeriasEvento(
  evento: FeriasEvento,
  now = new Date(),
): FeriasEventoCategoria {
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const start = new Date(evento.inicio)
  start.setHours(0, 0, 0, 0)
  const end = new Date(evento.fim)
  end.setHours(23, 59, 59, 999)

  const isFeriasRegistrada = evento.tipo === 'ferias_registrada'

  if (isFeriasRegistrada) {
    if (evento.status === 'EM_ANDAMENTO' || (start <= today && end >= today)) {
      return 'em_andamento'
    }
    if (evento.status === 'CONCLUIDO' || end < today) return 'historica'
    if (start > today || evento.status === 'SOLICITADO' || evento.status === 'APROVADO') {
      return 'programada'
    }
    return 'historica'
  }

  if (start > today) return 'programada'
  if (end >= today) return 'em_andamento'
  return 'historica'
}

export function listFeriasEventosProgramados(eventos: FeriasEvento[]): FeriasEvento[] {
  return eventos
    .filter(isFeriasEventoVisivel)
    .filter((evento) => classifyFeriasEvento(evento) === 'programada')
    .sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime())
}

export function visibleEventsOnDay(eventos: FeriasEvento[], day: Date | null): FeriasEvento[] {
  if (!day) return []
  return eventsOnDay(eventos, day).filter(isFeriasEventoVisivel)
}

export type FeriasDiaMarker = 'programada' | 'em_andamento' | 'solicitacao'

export function dayEventMarkers(eventos: FeriasEvento[], day: Date): FeriasDiaMarker[] {
  const markers = new Set<FeriasDiaMarker>()
  for (const evento of visibleEventsOnDay(eventos, day)) {
    const categoria = classifyFeriasEvento(evento)
    if (categoria === 'programada') {
      markers.add(
        evento.tipo === 'solicitacao_p6' || evento.tipo === 'solicitacao_p3'
          ? 'solicitacao'
          : 'programada',
      )
    } else if (categoria === 'em_andamento') {
      markers.add('em_andamento')
    }
  }
  return [...markers]
}

export const FERIAS_DIA_MARKER_CLASS: Record<FeriasDiaMarker, string> = {
  programada: 'bg-violet-400',
  em_andamento: 'bg-emerald-400',
  solicitacao: 'bg-sky-400',
}

export function formatPtDay(date: Date): string {
  return format(date, 'dd/MM/yyyy')
}

export function isSameMonthDay(day: Date, viewMonth: Date): boolean {
  return isSameMonth(day, viewMonth)
}

export function isToday(day: Date): boolean {
  return isSameDay(day, new Date())
}

export { addMonths, format, isSameDay, startOfMonth, subMonths } from 'date-fns'
